import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, ArrowLeft, Truck } from "lucide-react";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-ksy-ink placeholder-slate-400 focus:border-ksy-blue focus:outline-none focus:ring-2 focus:ring-blue-100 transition-colors";

  return (
    <div className="min-h-screen flex items-center justify-center bg-ksy-bg px-5" data-testid="admin-login-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-ksy-navy transition-colors mb-8" data-testid="back-to-site">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          <div className="flex items-center gap-2.5 mb-6">
            <span className="grid place-items-center w-10 h-10 rounded-lg bg-ksy-navy text-white"><Truck className="w-5 h-5" /></span>
            <div>
              <div className="font-display font-extrabold text-lg text-ksy-navy leading-tight">KS&amp;Y Dispatch</div>
              <div className="text-xs text-slate-400">Admin panel</div>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="login-email" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="login-password" />
            </div>
            {error && <p className="text-red-600 text-sm" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-ksy-blue text-white font-bold px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60" data-testid="login-submit-button">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
